import { useMemo } from "react";

export const usePagination = ({
  totalPages,
  currentPage,
  siblingCount = 1,
}: {
  totalPages: number;
  currentPage: number;
  siblingCount?: number;
}): (number | "ellipsis")[] => {
  return useMemo(() => {
    const totalPageNumbersToShow = siblingCount * 2 + 5;

    if (totalPages <= totalPageNumbersToShow) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const firstPageIndex = 1;
    const lastPageIndex = totalPages;
    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

    const shouldShowLeftEllipsis = leftSiblingIndex > 2;
    const shouldShowRightEllipsis = rightSiblingIndex < totalPages - 1;

    if (!shouldShowLeftEllipsis && shouldShowRightEllipsis) {
      const leftItemCount = 3 + 2 * siblingCount;
      const range = Array.from({ length: leftItemCount }, (_, i) => i + 1);
      return [...range, "ellipsis", lastPageIndex];
    }

    if (shouldShowLeftEllipsis && !shouldShowRightEllipsis) {
      const rightItemCount = 3 + 2 * siblingCount;
      const rangeStart = totalPages - rightItemCount + 1;
      const range = Array.from({ length: rightItemCount }, (_, i) => rangeStart + i);
      return [firstPageIndex, "ellipsis", ...range];
    }

    if (shouldShowLeftEllipsis && shouldShowRightEllipsis) {
      const middleRange = Array.from(
        { length: rightSiblingIndex - leftSiblingIndex + 1 },
        (_, i) => leftSiblingIndex + i,
      );
      return [firstPageIndex, "ellipsis", ...middleRange, "ellipsis", lastPageIndex];
    }

    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }, [totalPages, currentPage, siblingCount]);
};
